import React, { useState, useContext } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { useHistory, useLocation } from "react-router-dom";
import { 
  ChatCircleIcon, 
  AlignTopIcon, 
  CalendarDotsIcon, 
  ChartLineUpIcon, 
  PlusIcon 
} from "@phosphor-icons/react";
import clsx from "clsx";
import NewTicketModal from "../components/NewTicketModal";
import { TicketsContext } from "../context/Tickets/TicketsContext";
import { v4 as uuidv4 } from "uuid";

const useStyles = makeStyles((theme) => ({
  bottomNav: {
    position: "fixed",
    bottom: 20,
    left: "50%",
    transform: "translateX(-50%)",
    width: "95%",
    maxWidth: "400px",
    height: 65,
    backgroundColor: theme.mode === "light" ? "#ffffff" : theme.palette.background.paper,
    border: `1px solid ${theme.mode === "light" ? "#e2e2e2" : theme.palette.divider}`,
    borderRadius: 25,
    display: "flex",
    justifyContent: "space-around",
    alignItems: "center",
    padding: "6px",
    zIndex: 1000,
    boxShadow: theme.mode === "light" ? "0px 4px 4px rgba(0,0,0,0.04)" : "0px 4px 12px rgba(0,0,0,0.3)",
    [theme.breakpoints.up("md")]: {
      display: "none",
    },
  },
  navItem: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: 52,
    border: "none",
    outline: "none",
    cursor: "pointer",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    backgroundColor: "transparent",
    flexShrink: 0,
    WebkitTapHighlightColor: "transparent",
    overflow: "hidden",
  },
  inactiveItem: {
    flex: 1,
    maxWidth: 70,
    borderRadius: 19,
    color: theme.mode === "light" ? "#9ca3af" : "#6b7280",
    "&:hover": {
      color: theme.mode === "light" ? "#4b5563" : "#d1d5db",
      backgroundColor: theme.mode === "light" ? "#f9fafb" : "rgba(255,255,255,0.05)",
    }
  },
  activeItem: {
    padding: "0 20px",
    gap: 8,
    borderRadius: 19,
    border: `1px solid ${theme.mode === "light" ? "#e2e2e2" : theme.palette.divider}`,
    boxShadow: theme.mode === "light" ? "0 1px 2px rgba(0,0,0,0.05)" : "none",
    background: theme.mode === "light" 
      ? "linear-gradient(180deg, rgba(238,238,238,1) 0%, rgba(255,255,255,1) 100%)"
      : `linear-gradient(180deg, ${theme.palette.background.default} 0%, ${theme.palette.background.paper} 100%)`,
    color: theme.palette.primary.main,
  },
  iconWrapper: {
    flexShrink: 0,
    display: "flex",
  },
  label: {
    fontWeight: 600,
    fontSize: "12px",
    letterSpacing: "0.025em",
    whiteSpace: "nowrap",
    fontFamily: "'Poppins', sans-serif",
    animation: "$fadeInSlide 0.3s ease forwards",
  },
  "@keyframes fadeInSlide": {
    "0%": { opacity: 0, transform: "translateX(-5px)" },
    "100%": { opacity: 1, transform: "translateX(0)" }
  }
}));

const MobileNavbar = () => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);
  const { setCurrentTicket } = useContext(TicketsContext);

  // Regra para ocultar dentro do chat ativo
  const isChatActive = location.pathname.startsWith("/tickets/") && location.pathname !== "/tickets";

  if (isChatActive) return null;

  const handleCloseNewTicketModal = (newTicket) => {
    setNewTicketModalOpen(false);
    if (newTicket && newTicket.uuid) {
      const code = uuidv4();
      setCurrentTicket({ id: newTicket.id, uuid: newTicket.uuid, code });
      history.push(`/tickets/${newTicket.uuid}`);
    }
  };

  const navItems = [
    { id: "tickets", icon: ChatCircleIcon, label: "Tickets", path: "/tickets" },
    { id: "schedules", icon: CalendarDotsIcon, label: "Agendas", path: "/schedules" },
    { id: "new", icon: PlusIcon, label: "Novo", action: () => setNewTicketModalOpen(true) },
    { id: "dashboard", icon: ChartLineUpIcon, label: "Painel", path: "/" },
    { id: "kanban", icon: AlignTopIcon, label: "Kanban", path: "/kanban" },
  ];

  return (
    <>
    <div className={classes.bottomNav}>
      {navItems.map((item) => {
        const isActive = item.path ? location.pathname === item.path : false;
        const Icon = item.icon;

        return (
          <button
            key={item.id}
            onClick={() => item.action ? item.action() : history.push(item.path)}
            className={clsx(
              classes.navItem,
              isActive ? classes.activeItem : classes.inactiveItem
            )}
            aria-pressed={isActive}
          >
            <div className={classes.iconWrapper}>
              <Icon 
                size={22} 
                weight={item.id === "new" ? "bold" : (isActive ? "bold" : "regular")} 
              />
            </div>
            {isActive && item.label && (
              <span className={classes.label}>
                {item.label}
              </span>
            )}
          </button>
        );
      })}
    </div>
    {newTicketModalOpen && (
      <NewTicketModal
        modalOpen={newTicketModalOpen}
        onClose={handleCloseNewTicketModal}
      />
    )}
    </>
  );
};

export default MobileNavbar;